/**
 * VENOA: makes each accordion block behave as one exclusive group — at most
 * one row open at a time, and the open row can be closed again (0 or 1 open).
 *
 * Strictly scoped: listeners are bound per accordion container and only ever
 * touch rows belonging to that same container. Accordions elsewhere on the
 * page (header drawer, footer menus, filters, a second accordion block) are
 * never affected, and a nested accordion keeps its own group.
 *
 * Siblings are closed on the summary click, i.e. before the browser opens the
 * clicked row. Reacting to `toggle` instead would be too late: that event is
 * dispatched asynchronously, so several rapid clicks could leave the wrong
 * row open. The `toggle` listener stays as a fallback for rows opened by
 * script; it runs in the capture phase because `toggle` does not bubble.
 */
(() => {
  const GROUP_SELECTOR = '[data-venoa-accordion-group]';
  const ROW_SELECTOR = ':scope > accordion-custom > details';
  const bound = new WeakSet();

  const closeSiblings = (group, row) => {
    for (const other of group.querySelectorAll(ROW_SELECTOR)) {
      if (other !== row && other.open) other.open = false;
    }
  };

  const rowFor = (group, element) => {
    const row = element?.closest('details');
    if (!(row instanceof HTMLDetailsElement)) return null;
    if (row.closest(GROUP_SELECTOR) !== group) return null;
    if (row.parentElement?.parentElement !== group) return null;
    return row;
  };

  const onClick = (event) => {
    if (event.defaultPrevented) return;

    const summary = event.target instanceof Element ? event.target.closest('summary') : null;
    if (!summary) return;

    const group = event.currentTarget;
    const row = rowFor(group, summary);
    // An open row is about to close — that leaves nothing to collapse.
    if (!row || row.open) return;

    closeSiblings(group, row);
  };

  const onToggle = (event) => {
    const group = event.currentTarget;
    const row = rowFor(group, event.target);
    if (!row || !row.open) return;

    closeSiblings(group, row);
  };

  const setup = (group) => {
    if (bound.has(group)) return;
    bound.add(group);
    group.addEventListener('click', onClick);
    group.addEventListener('toggle', onToggle, true);
  };

  const init = (root = document) => {
    if (root instanceof Element && root.matches(GROUP_SELECTOR)) setup(root);
    root.querySelectorAll?.(GROUP_SELECTOR).forEach(setup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  // Theme editor and Section Rendering API replace markup at runtime.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target));
})();
